import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import * as repo from './repo';
import * as sync from './sync';
import * as media from './media';
import { StorageUnavailableError, setIdentity } from './db';
import transport, { configure as configureApi } from '../fairTrackerApi';
import { getSpaceId } from '../../../hive/getSpaceId';

/**
 * The app's single in-memory copy of the local database, plus the actions that
 * mutate it.
 *
 * Screens never touch repo.js or IndexedDB directly — they read this and call
 * these actions. That keeps one rule enforceable in one place: a mutation
 * writes local storage, updates this state, and queues sync, in that order,
 * and nothing on the capture path awaits a network.
 *
 * State updates are optimistic in the sense that they are immediate, but they
 * are not speculative — the local write has already succeeded by the time the
 * UI changes. If it fails we surface it rather than pretending.
 */

const FairTrackerContext = createContext(null);

export function useFairTracker() {
  const ctx = useContext(FairTrackerContext);
  if (!ctx) throw new Error('useFairTracker must be used inside <FairTrackerProvider>');
  return ctx;
}

const EMPTY = {
  loaded: false,
  fairs: [],
  suppliers: [],
  settings: null,
  currentFair: '',
  welcomed: false,
};

export function FairTrackerProvider({ userInfo, children }) {
  /* Point the data layer at THIS account's partition before anything can read
     it (see schema.js on why the local store is partitioned at all).

     In the render body rather than an effect, deliberately: a child's effects
     run before its parent's, so an effect here would land after a screen had
     already touched the repo on mount — and the whole value of the partition is
     that there is never a window in which a read could reach the wrong account.
     setIdentity is idempotent, and a cheap string compare when nothing changed,
     which is every render but the first.

     spaceId, not just userId: the server scopes app data by (spaceId,
     ownerUserId), so one account in two workspaces is two distinct scopes and
     therefore has to be two distinct local stores. */
  const userId = (userInfo && userInfo.userId) || null;
  const spaceId = getSpaceId(userInfo) || userId;
  setIdentity(userId ? { userId, spaceId } : null);

  const [state, setState] = useState(EMPTY);
  const [syncInfo, setSyncInfo] = useState(sync.syncState());
  const [storageError, setStorageError] = useState(null);
  const mounted = useRef(true);

  useEffect(() => () => { mounted.current = false; }, []);

  const apply = useCallback((patch) => {
    if (mounted.current) setState((s) => ({ ...s, ...patch }));
  }, []);

  /** Re-read everything from IndexedDB. Cheap — these are small records. */
  const reload = useCallback(async () => {
    try {
      const data = await repo.load();
      apply({ ...data, loaded: true });
      return data;
    } catch (e) {
      if (e instanceof StorageUnavailableError) setStorageError(e.message);
      apply({ loaded: true });
      return null;
    }
  }, [apply]);

  /* Wire the real transport. The sync engine defaults to offlineTransport, so
     until this runs nothing can reach the network — which is also what keeps
     the jest tests and the self-check hermetic. */
  useEffect(() => {
    configureApi(userInfo);
    sync.setTransport(transport);
  }, [userInfo]);

  /* A partition switch — a different account, or the same account in a
     different workspace — must not leave the previous one's records on screen
     while the new partition loads. Declared BEFORE the boot effect so the reset
     runs first and the reload that follows repopulates from the right store.

     releaseAllUrls matters as much as the state reset: media.js holds a
     module-level Map of blob: URLs keyed by media id, and every one of them
     points at a Blob read out of the partition being left behind. */
  useEffect(() => {
    media.releaseAllUrls();
    sync.resetState();
    setState(EMPTY);
  }, [userId, spaceId]);

  /* Boot: load, then let sync run in the background. Load never waits on sync.

     NO PROFILE PREFILL, by product decision — the buyer types their own company
     card on the welcome screen rather than confirming guessed values. (Spec § 1
     asks for a prefill from the BeeNexa account; it is deliberately off.) */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await reload();
      if (cancelled) return;
      sync.refreshPendingCount();
    })();
    return () => { cancelled = true; };
    // userInfo's object identity changes on every permissions refresh, so this
    // keys on the partition instead — which is also exactly what has to force a
    // reload: either half of (userId, spaceId) changing means a different store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, spaceId, reload]);

  /* Sync indicator + auto-sync triggers. */
  useEffect(() => {
    const off = sync.onSyncChange((s) => { if (mounted.current) setSyncInfo(s); });
    const stop = sync.startAutoSync();
    return () => { off(); stop(); };
  }, []);

  /* A sync that pulled changes has to be reflected in what the UI holds. */
  useEffect(() => {
    if (syncInfo.status === 'idle' && syncInfo.lastSyncAt) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncInfo.lastSyncAt]);

  /** Wrap a repo mutation: run it, reconcile state, kick sync, surface errors. */
  const mutate = useCallback(
    async (fn, optimistic) => {
      try {
        if (optimistic) apply(optimistic);
        const result = await fn();
        await reload();
        sync.syncNow();
        return result;
      } catch (e) {
        await reload();
        const message =
          e instanceof StorageUnavailableError
            ? e.message
            : 'Could not save on this device. Nothing was lost — try again.';
        setStorageError(message);
        throw e;
      }
    },
    [apply, reload],
  );

  const actions = useMemo(
    () => ({
      reload,
      saveFair: (fair) => mutate(() => repo.saveFair(fair)),
      deleteFair: (id) => mutate(() => repo.deleteFair(id)),
      saveSupplier: (supplier) => mutate(() => repo.saveSupplier(supplier)),
      deleteSupplier: (id) => mutate(() => repo.deleteSupplier(id)),
      saveSettings: (settings) => mutate(() => repo.saveSettings(settings)),
      createFirstFair: (fair) => mutate(() => repo.createFirstFair(fair)),
      setWelcomed: () => mutate(() => repo.setWelcomed()),
      setCurrentFair: (id) => mutate(() => repo.setCurrentFair(id)),
      deleteAllData: () => mutate(() => repo.deleteAllData()),
      getSupplier: repo.getSupplier,
      counts: repo.counts,
      syncNow: sync.syncNow,
      dismissStorageError: () => setStorageError(null),
    }),
    [mutate, reload],
  );

  /** The prototype's `vis()`: the current fair's suppliers, or all of them. */
  const visibleSuppliers = useMemo(() => {
    if (state.currentFair === 'all') return state.suppliers;
    return state.suppliers.filter((s) => s.fairId === state.currentFair);
  }, [state.suppliers, state.currentFair]);

  const fair = useMemo(
    () => state.fairs.find((f) => f.id === state.currentFair) || null,
    [state.fairs, state.currentFair],
  );

  const value = useMemo(
    () => ({ ...state, fair, visibleSuppliers, sync: syncInfo, storageError, ...actions }),
    [state, fair, visibleSuppliers, syncInfo, storageError, actions],
  );

  return <FairTrackerContext.Provider value={value}>{children}</FairTrackerContext.Provider>;
}

export default FairTrackerProvider;
